import {
  PracticeClosure,
  PracticeClosureCreatePayload,
  PracticeClosureGetParams,
  PracticeClosureUpdatePayload,
} from '../../types/closures/types';
import { getCustomerIdFromToken } from '../../utils/getCustomerIdFromToken';
import { get, post, put, remove } from '../apiService';

const BASE_URL = 'resourceMatrix/practice_closure';

export const getPracticeClosures = async (
  params: PracticeClosureGetParams & { marketId?: string }
): Promise<PracticeClosure[]> => {
  const customer = getCustomerIdFromToken();
  try {
    const queryParams = new URLSearchParams({
      customerId: customer,
      ...(params.marketId && { marketId: params.marketId }),
      ...(params.startDate && { startDate: params.startDate }),
      ...(params.endDate && { endDate: params.endDate }),
    });

    // Append each practiceId as a separate param (mirrors getPractices.ts marketIds pattern)
    if (params.practiceIds?.length) {
      params.practiceIds.forEach((id) => queryParams.append('practiceIds', id));
    }

    const url = `${BASE_URL}/list?${queryParams.toString()}`;
    const response = await get<{
      status: string;
      closures: PracticeClosure[];
    }>(url);

    if (response.status === 'success') {
      return response.closures;
    } else {
      throw new Error(
        'Failed to fetch closures: Invalid response status'
      );
    }
  } catch (error) {
    console.error('Failed to fetch practice closures:', error);
    throw new Error('Failed to fetch practice closures');
  }
};

export const createPracticeClosure = async (
  payload: PracticeClosureCreatePayload
): Promise<PracticeClosure> => {
  const customer = getCustomerIdFromToken();
  try {
    const url = `${BASE_URL}/create?customerId=${customer}`;
    return await post<PracticeClosure>(url, { ...payload });
  } catch (error) {
    console.error('Failed to create practice closure:', error);
    throw new Error('Failed to create practice closure');
  }
};

export const updatePracticeClosure = async (
  payload: PracticeClosureUpdatePayload
): Promise<PracticeClosure> => {
  const customer = getCustomerIdFromToken();
  try {
    const url = `${BASE_URL}/update?customerId=${customer}`;
    return await put<PracticeClosure>(url, { ...payload });
  } catch (error) {
    console.error('Failed to update practice closure:', error);
    throw new Error('Failed to update practice closure');
  }
};

export const deletePracticeClosure = async (
  ids: string[]
): Promise<void> => {
  const customer = getCustomerIdFromToken();
  try {
    const queryParams = new URLSearchParams({
      customerId: customer,
      ids: ids.join('&'), // Use `&` separator
    });

    const url = `${BASE_URL}/delete?${queryParams.toString()}`;
    await remove<void>(url);
  } catch (error) {
    console.error('Failed to delete practice closure:', error);
    throw new Error('Failed to delete practice closure');
  }
};
